/**
 * StyleSelector Reducer
 *
 * State management for the StyleSelector component.
 */

import type { StyleInfo } from '../../types';

// ============================================================================
// State
// ============================================================================

export interface HoverPreviewState {
  url: string;
  name: string;
  position: { x: number; y: number };
}

export interface LightboxState {
  url: string;
  name: string;
  category: string;
}

export interface StyleSelectorState {
  styles: StyleInfo[];
  categories: string[];
  activeCategory: string | null;
  isExpanded: boolean;
  searchQuery: string;
  isLoading: boolean;
  showOnlyFavorites: boolean;
  visibleCount: number;
  hoverPreview: HoverPreviewState | null;
  lightbox: LightboxState | null;
}

// ============================================================================
// Actions
// ============================================================================

export type StyleSelectorAction =
  | { type: 'SET_STYLES'; payload: StyleInfo[] }
  | { type: 'SET_CATEGORIES'; payload: string[] }
  | { type: 'STYLES_LOADED'; payload: { styles: StyleInfo[]; categories: string[] } }
  | { type: 'SET_ACTIVE_CATEGORY'; payload: string | null }
  | { type: 'SET_IS_EXPANDED'; payload: boolean }
  | { type: 'TOGGLE_EXPANDED' }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'SET_SHOW_ONLY_FAVORITES'; payload: boolean }
  | { type: 'TOGGLE_SHOW_ONLY_FAVORITES' }
  | { type: 'SET_VISIBLE_COUNT'; payload: number }
  | { type: 'INCREMENT_VISIBLE_COUNT'; payload: number }
  | { type: 'RESET_VISIBLE_COUNT'; payload: number }
  | { type: 'SET_HOVER_PREVIEW'; payload: HoverPreviewState | null }
  | { type: 'CLEAR_HOVER_PREVIEW' }
  | { type: 'OPEN_LIGHTBOX'; payload: LightboxState }
  | { type: 'CLOSE_LIGHTBOX' };

// ============================================================================
// Initial State
// ============================================================================

export const initialStyleSelectorState: StyleSelectorState = {
  styles: [],
  categories: [],
  activeCategory: null,
  isExpanded: false,
  searchQuery: '',
  isLoading: true,
  showOnlyFavorites: false,
  visibleCount: 24,
  hoverPreview: null,
  lightbox: null,
};

// ============================================================================
// Reducer
// ============================================================================

export function styleSelectorReducer(
  state: StyleSelectorState,
  action: StyleSelectorAction
): StyleSelectorState {
  switch (action.type) {
    case 'SET_STYLES':
      return { ...state, styles: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'STYLES_LOADED':
      return { ...state, styles: action.payload.styles, categories: action.payload.categories };
    case 'SET_ACTIVE_CATEGORY':
      return { ...state, activeCategory: action.payload };
    case 'SET_IS_EXPANDED':
      return { ...state, isExpanded: action.payload };
    case 'TOGGLE_EXPANDED':
      return { ...state, isExpanded: !state.isExpanded };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SHOW_ONLY_FAVORITES':
      return { ...state, showOnlyFavorites: action.payload };
    case 'TOGGLE_SHOW_ONLY_FAVORITES':
      return { ...state, showOnlyFavorites: !state.showOnlyFavorites };
    case 'SET_VISIBLE_COUNT':
      return { ...state, visibleCount: action.payload };
    case 'INCREMENT_VISIBLE_COUNT':
      return { ...state, visibleCount: state.visibleCount + action.payload };
    case 'RESET_VISIBLE_COUNT':
      return { ...state, visibleCount: action.payload };
    case 'SET_HOVER_PREVIEW':
      return { ...state, hoverPreview: action.payload };
    case 'CLEAR_HOVER_PREVIEW':
      return { ...state, hoverPreview: null };
    case 'OPEN_LIGHTBOX':
      return { ...state, lightbox: action.payload, hoverPreview: null };
    case 'CLOSE_LIGHTBOX':
      return { ...state, lightbox: null };
    default:
      return state;
  }
}
